import { ClientsService } from './../../../shared/services/clients.service';
import { SitesService } from './../../../shared/services/sites.service';
import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { AddressesService } from '@services/addresses.service';
import { Address } from '@classes/address';
import { Site } from '@classes/site';
import { Client } from '@classes/client';

@Component({
  selector: 'app-site-edit',
  templateUrl: './site-edit.component.html',
  styleUrls: ['./site-edit.component.scss'],
})
export class SiteEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() site: Site;
  clients: Client[];
  senderAddresses: Address[] = [];
  identifierPattern = '^[0-9]{0,50}$';

  stringMaxLength = 50;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private sitesSrv: SitesService,
    private clientsSrv: ClientsService,
    private addressesSrv: AddressesService,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  initSub(): void {
    this.isLoading = true;

    // Les adresses ne sont rattachables qu'à un site existant.
    const addresses$ = this.site?.id
      ? this.addressesSrv.getListForSite(this.site.id)
      : of([] as Address[]);

    forkJoin([this.clientsSrv.getList(), addresses$]).subscribe(
      ([clientsFromSrv, addressesFromSrv]) => {
        this.clients = clientsFromSrv;
        this.senderAddresses = addressesFromSrv.filter(
          (address) => address.isSender,
        );
        this.isLoading = false;
        this.initForm();
      },
    );
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.site) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const siteCopy: Site = JSON.parse(JSON.stringify(this.site));
    siteCopy.name = this.editForm.value.name;
    siteCopy.clients = this.editForm.value.clients;
    siteCopy.documentUrl = this.editForm.value.documentUrl;
    siteCopy.siren = this.editForm.value.siren;
    siteCopy.allowOtherAddress = this.editForm.value.allowOtherAddress;
    siteCopy.transmissionOnly = this.editForm.value.transmissionOnly;
    siteCopy.addressId = this.editForm.value.address?.id ?? null;
    siteCopy.address = undefined;

    this.sitesSrv.put(siteCopy).subscribe(() => (this.isLoading = false));
  }

  private create(): void {
    this.site = new Site(
      this.editForm.value.name,
      this.editForm.value.clients,
      this.editForm.value.otherSite,
      this.editForm.value.documentUrl,
      this.editForm.value.siren,
      this.editForm.value.allowOtherAddress,
      this.editForm.value.transmissionOnly,
    );

    this.sitesSrv.post(this.site).subscribe(
      () => (this.isLoading = false),
      () => (this.site = null),
    );
  }

  private initForm(): void {
    let name = '';
    let clients: Client[] = [];
    let otherSite = false;
    let documentUrl = '';
    let siren = '';
    let allowOtherAddress: boolean | null = true;
    let transmissionOnly = false;

    if (this.site) {
      name = this.site.name;
      clients = this.site.clients || [];
      otherSite = this.site.otherSite;
      documentUrl = this.site.documentUrl;
      siren = this.site.siren;
      transmissionOnly = this.site.transmissionOnly ?? false;
      allowOtherAddress = this.site.allowOtherAddress ?? null;
    }

    this.editForm = this.fb.group({
      name: [
        name,
        [Validators.required, Validators.maxLength(this.stringMaxLength)],
      ],
      clients: [clients, Validators.required],
      otherSite: [otherSite],
      documentUrl: [documentUrl],
      siren: [siren, [Validators.maxLength(9), Validators.minLength(9)]],
      allowOtherAddress: [allowOtherAddress],
      transmissionOnly: [transmissionOnly],
    });

    if (this.site?.id) {
      this.editForm.addControl(
        'address',
        this.fb.control(this.resolveDefaultAddress()),
      );
    }
  }

  private resolveDefaultAddress(): Address {
    return (
      this.senderAddresses.find(
        (address) => address.id === this.site.address?.id,
      ) ?? null
    );
  }

  compareAddresses(a: Address, b: Address): boolean {
    return a?.id === b?.id;
  }
}
